import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import SplitType from 'split-type';
import MagneticButton from '../ui/MagneticButton';
import styles from './Hero.module.scss';
import portraitImg from '../../assets/images/Rashika_nagotra.jpeg';

const Hero: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);
  
  useEffect(() => {
    const ctx = gsap.context(() => {
      if (titleRef.current) {
        gsap.from(titleRef.current, {
          y: 40,
          opacity: 0,
          duration: 1.2,
          ease: 'power4.out',
          delay: 0.2
        });
      }

      if (descRef.current) {
        gsap.from(descRef.current, {
          y: 30,
          opacity: 0,
          duration: 1,
          ease: 'power3.out',
          delay: 0.6
        });
      }
      
      const blob1 = document.querySelector(`.${styles.glowBlob1}`);
      const blob2 = document.querySelector(`.${styles.glowBlob2}`);
      
      const setBlob1X = gsap.quickSetter(blob1, "x", "px");
      const setBlob1Y = gsap.quickSetter(blob1, "y", "px");
      const setBlob2X = gsap.quickSetter(blob2, "x", "px");
      const setBlob2Y = gsap.quickSetter(blob2, "y", "px");

      const handleParallax = (e: MouseEvent) => {
        const xObj = e.clientX / window.innerWidth - 0.5;
        const yObj = e.clientY / window.innerHeight - 0.5;
        
        setBlob1X(xObj * 100);
        setBlob1Y(yObj * 100);
        setBlob2X(xObj * -150);
        setBlob2Y(yObj * -150);
      };

      window.addEventListener('mousemove', handleParallax);
      
      return () => {
        window.removeEventListener('mousemove', handleParallax);
      };
    }, containerRef);
    
    return () => ctx.revert();
  }, []);

  return (
    <section className={styles.heroSection} id="home" ref={containerRef}>
      <div className={`${styles.animatedBackground} agency-grid-bg`}>
        <div className={styles.glowBlob1}></div>
        <div className={styles.glowBlob2}></div>
      </div>
      
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.tagLine}>
            <span className={styles.tagDot}></span>
            RESULTS-DRIVEN DIGITAL AGENCY
          </div>

          <h1 className={styles.title} ref={titleRef}>
            Elevating Brands Through <br />
            <span className={styles.highlight}>Strategic SEO</span> & Digital Growth.
          </h1>
          
          <p className={styles.description} ref={descRef}>
            Founder & Digital Marketing Head of Rash Digital. We help ambitious businesses achieve sustainable growth, dominate local search, and build premium visual identities.
          </p>
          
          <div className={styles.ctaGroup}>
            <MagneticButton className={styles.primaryBtn}>
              Discuss a Project
            </MagneticButton>
            <a href="#case-studies" className={styles.secondaryBtn}>
              View Case Studies
            </a>
          </div>
        </div>

        <div className={styles.imageContainer}>
          <div className={styles.imageWrapper}>
            {/* SVG Background Circle */}
            <div className={styles.decoCircle}>
              <svg width="450" height="450" viewBox="0 0 100 100" fill="none">
                <circle cx="50" cy="50" r="48" stroke="rgba(16, 185, 129, 0.15)" strokeWidth="0.5" strokeDasharray="3 3" />
                <circle cx="50" cy="50" r="44" stroke="rgba(16, 185, 129, 0.1)" strokeWidth="0.5" />
              </svg>
            </div>
            
            <div className={styles.imagePlaceholder}>
              <img src={portraitImg} alt="Rashika Nagotra" className={styles.portraitImage} />
            </div>

            {/* Floating Badges */}
            <div className={`${styles.floatBadge} ${styles.badge1}`}>
              <span className={styles.badgeIcon}>📈</span>
              <div>
                <strong>SEO Specialist</strong>
                <p>Google Top Rankings</p>
              </div>
            </div>

            <div className={`${styles.floatBadge} ${styles.badge2}`}>
              <span className={styles.badgeIcon}>🔥</span>
              <div>
                <strong>Meta Ads</strong>
                <p>High ROI Campaigns</p>
              </div>
            </div>

            <div className={`${styles.floatBadge} ${styles.badge3}`}>
              <span className={styles.badgeIcon}>🎨</span>
              <div>
                <strong>Creative Design</strong>
                <p>Visual Brand Value</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
